import React, { useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import {
  Card,
  Form,
  Select,
  Upload,
  Button,
  Table,
  Alert,
  Space,
  Row,
  Col,
  Spin,
  Typography,
  message,
  Divider,
} from "antd";
import {
  InboxOutlined,
  DownloadOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import API_BASE_URL from "../../config/api";

const { Title, Text } = Typography;
const { Dragger } = Upload;

const ContactImport = ({ onClose, onImportSuccess }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [previewData, setPreviewData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedBunch, setSelectedBunch] = useState("");
  const [contactBunches, setContactBunches] = useState([]);
  const [importErrors, setImportErrors] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Fetch contact bunches
  React.useEffect(() => {
    fetchContactBunches();
  }, []);

  const fetchContactBunches = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/contact-bunches`, {
        headers: {
          Authorization: token,
        },
      });
      setContactBunches(response.data);
    } catch (err) {
      console.error("Failed to fetch contact bunches:", err);
      message.error("Failed to fetch contact bunches");
    }
  };

  const handleFileChange = (info) => {
    const { file: selectedFile } = info;

    if (selectedFile) {
      const isExcel =
        selectedFile.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        selectedFile.type === "application/vnd.ms-excel" ||
        selectedFile.type === "text/csv";

      if (!isExcel) {
        message.error("You can only upload Excel or CSV files!");
        return;
      }

      setFile(selectedFile);
      setError("");
      setSuccess("");
      setImportErrors([]);
      previewExcelFile(selectedFile);
    }
  };

  const previewExcelFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        // Show first 5 rows as preview
        setPreviewData(jsonData.slice(0, 5));
        setTotalRecords(jsonData.length);
      } catch (err) {
        setError("Error reading Excel file. Please check the format.");
        message.error("Error reading Excel file. Please check the format.");
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleImport = async () => {
    if (!file) {
      message.error("Please select a file to import");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");
    setImportErrors([]);

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        const contacts = jsonData
          .map((row) => {
            let rawPhone = row.Phone || row.phone || row["Phone Number"] || "";
            rawPhone = String(rawPhone).trim();
            let phone = rawPhone.replace(/[^\d+]/g, "");
            if (!phone.startsWith("+91")) {
              phone = phone.replace(/^\+?91/, "");
              phone = "+91" + phone;
            }
            return {
              name: row.Name || row.name || row["Full Name"] || "",
              phone,
              email: row.Email || row.email || row["Email Address"] || "",
              address: row.Address || row.address || row["Home Address"] || "",
              language: row.Language || row.language || "",
              notes: row.Notes || row.notes || row["Additional Notes"] || "",
            };
          })
          .filter((contact) => contact.name.trim() !== "");
        if (contacts.length === 0) {
          message.error("No valid contacts found in the file");
          setLoading(false);
          return;
        }

        // Import contacts with contact bunch id
        const token = localStorage.getItem("token");
        const response = await axios.post(
          `${API_BASE_URL}/contacts/import`,
          { contacts, contact_bunch_id: selectedBunch || null },
          {
            headers: {
              Authorization: token,
            },
          }
        );

        message.success(
          `Successfully imported ${response.data.imported} contacts`
        );
        setSuccess(`Successfully imported ${response.data.imported} contacts`);

        if (response.data.errors && response.data.errors.length > 0) {
          setImportErrors(response.data.errors);
        }

        setTimeout(() => {
          onImportSuccess();
        }, 2000);
      } catch (err) {
        const errorMessage =
          err.response?.data?.message || "Error importing contacts";
        setError(errorMessage);
        message.error(errorMessage);
      } finally {
        setLoading(false);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const downloadSampleFile = () => {
    const sampleData = [
      {
        Name: "John Doe",
        Phone: "+1234567890",
      },
      {
        Name: "Jane Smith",
        Phone: "+1987654321",
      },
      {
        Name: "Robert Johnson",
        Phone: "+1122334455",
      },
      {
        Name: "Alice Brown",
        Phone: "+1555666777",
      },
      {
        Name: "Michael Davis",
        Phone: "+1444333222",
      },
    ];

    const worksheet = XLSX.utils.json_to_sheet(sampleData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Contacts");

    // Generate Excel file and trigger download
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sample_contacts_import.xlsx";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Table columns for preview
  const previewColumns =
    previewData.length > 0
      ? Object.keys(previewData[0]).map((key) => ({
          title: key,
          dataIndex: key,
          key: key,
          render: (text) => text || "-",
        }))
      : [];

  return (
    <Spin spinning={loading}>
      <Card
        // title="Import Contacts from Excel"
        style={{ maxWidth: 800, margin: "0 auto" }}
      >
        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            closable
            onClose={() => setError("")}
          />
        )}

        {success && (
          <Alert
            message="Success"
            description={success}
            type="success"
            showIcon
            style={{ marginBottom: 16 }}
            closable
            onClose={() => setSuccess("")}
          />
        )}

        {importErrors.length > 0 && (
          <Alert
            message="Import Issues"
            description={
              <ul
                style={{
                  marginBottom: 0,
                  maxHeight: 200,
                  overflowY: "auto",
                  paddingLeft: 20,
                }}
              >
                {importErrors.map((err, index) => (
                  <li key={index}>{err}</li>
                ))}
              </ul>
            }
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            closable
            onClose={() => setImportErrors([])}
          />
        )}

        <Form layout="vertical">
          <Form.Item
            label="Select Contact Bunch"
            extra="Contacts will be added to this bunch during import"
            required
          >
            <Select
              placeholder="Select a contact bunch"
              value={selectedBunch}
              onChange={setSelectedBunch}
              allowClear
            >
              {contactBunches.map((bunch) => (
                <Select.Option key={bunch.id} value={bunch.id}>
                  {bunch.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Upload Excel File">
            <Dragger
              accept=".xlsx,.xls,.csv"
              beforeUpload={(file) => {
                handleFileChange({ file });
                return false; // Prevent auto upload
              }}
              onRemove={() => {
                setFile(null);
                setPreviewData([]);
              }}
              fileList={file ? [file] : []}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined style={{ fontSize: 48, color: "#1890ff" }} />
              </p>
              <p className="ant-upload-text">
                Click or drag file to this area to upload
              </p>
              <p className="ant-upload-hint">
                Support for .xlsx, .xls, .csv files. Required columns: Name,
                Phone (optional), Email (optional)
              </p>
            </Dragger>
          </Form.Item>

          <Form.Item>
            <Button
              type="dashed"
              icon={<DownloadOutlined />}
              onClick={downloadSampleFile}
              block
            >
              Download Sample File
            </Button>
          </Form.Item>

          {/* Remove preview table and show message with total records */}
          {totalRecords > 0 && (
            <div style={{ marginTop: 16 }}>
              <Text strong>
                This file has {totalRecords} contact
                {totalRecords > 1 ? "s" : ""}.
              </Text>
            </div>
          )}
          {/* {previewData.length > 0 && (
            <Card
              title="Preview (First 5 rows)"
              size="small"
              style={{ marginTop: 16 }}
            >
              <Table
                dataSource={previewData}
                columns={previewColumns}
                pagination={false}
                size="small"
                scroll={{ x: "max-content" }}
              />
            </Card>
          )} */}
        </Form>

        <Divider />

        <Row gutter={16} justify="end">
          <Col>
            <Button onClick={onClose} disabled={loading}>
              Cancel
            </Button>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<UploadOutlined />}
              onClick={handleImport}
              disabled={!file}
              loading={loading}
            >
              Import Contacts
            </Button>
          </Col>
        </Row>
      </Card>
    </Spin>
  );
};

export default ContactImport;

// import React, { useState, useEffect } from "react";
// import axios from "axios";
// import * as XLSX from "xlsx";
// import API_BASE_URL from "../../config/api";

// const ContactImportModal = ({ show, setShow, onImportSuccess }) => {
//   const [file, setFile] = useState(null);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState("");
//   const [success, setSuccess] = useState("");
//   const [previewData, setPreviewData] = useState([]);
//   const [selectedBunch, setSelectedBunch] = useState("");
//   const [contactBunches, setContactBunches] = useState([]);
//   const [importErrors, setImportErrors] = useState([]);

//   // Fetch contact bunches
//   useEffect(() => {
//     const fetchContactBunches = async () => {
//       try {
//         const token = localStorage.getItem("token");
//         const response = await axios.get(`${API_BASE_URL}/contact-bunches`, {
//           headers: { Authorization: token },
//         });
//         setContactBunches(response.data);
//       } catch (err) {
//         setError("Failed to fetch contact bunches");
//       }
//     };
//     fetchContactBunches();
//   }, []);

//   const handleFileChange = (e) => {
//     const selectedFile = e.target.files[0];
//     if (!selectedFile) return;

//     const isExcel =
//       selectedFile.type ===
//         "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
//       selectedFile.type === "application/vnd.ms-excel" ||
//       selectedFile.type === "text/csv";

//     if (!isExcel) {
//       setError("You can only upload Excel or CSV files!");
//       return;
//     }

//     setFile(selectedFile);
//     setError("");
//     setSuccess("");
//     setImportErrors([]);
//     previewExcelFile(selectedFile);
//   };

//   const previewExcelFile = (file) => {
//     const reader = new FileReader();
//     reader.onload = (e) => {
//       try {
//         const data = new Uint8Array(e.target.result);
//         const workbook = XLSX.read(data, { type: "array" });
//         const sheetName = workbook.SheetNames[0];
//         const worksheet = workbook.Sheets[sheetName];
//         const jsonData = XLSX.utils.sheet_to_json(worksheet);
//         setPreviewData(jsonData.slice(0, 5));
//       } catch {
//         setError("Error reading Excel file. Please check the format.");
//       }
//     };
//     reader.readAsArrayBuffer(file);
//   };

//   const handleImport = async () => {
//     if (!file) {
//       setError("Please select a file to import");
//       return;
//     }

//     setLoading(true);
//     setError("");
//     setSuccess("");
//     setImportErrors([]);

//     const reader = new FileReader();
//     reader.onload = async (e) => {
//       try {
//         const data = new Uint8Array(e.target.result);
//         const workbook = XLSX.read(data, { type: "array" });
//         const sheetName = workbook.SheetNames[0];
//         const worksheet = workbook.Sheets[sheetName];
//         const jsonData = XLSX.utils.sheet_to_json(worksheet);

//         const contacts = jsonData
//           .map((row) => {
//             let rawPhone =
//               row.Phone || row.phone || row["Phone Number"] || "";
//             rawPhone = String(rawPhone).trim();
//             let phone = rawPhone.replace(/[^\d+]/g, "");
//             if (!phone.startsWith("+91")) {
//               phone = phone.replace(/^\+?91/, "");
//               phone = "+91" + phone;
//             }
//             return {
//               name: row.Name || row.name || row["Full Name"] || "",
//               phone,
//               email: row.Email || row.email || row["Email Address"] || "",
//               address: row.Address || row.address || row["Home Address"] || "",
//               language: row.Language || row.language || "",
//               notes:
//                 row.Notes || row.notes || row["Additional Notes"] || "",
//             };
//           })
//           .filter((c) => c.name.trim() !== "");

//         if (contacts.length === 0) {
//           setError("No valid contacts found in the file");
//           setLoading(false);
//           return;
//         }

//         const token = localStorage.getItem("token");
//         const response = await axios.post(
//           `${API_BASE_URL}/contacts/import`,
//           { contacts, contact_bunch_id: selectedBunch || null },
//           { headers: { Authorization: token } }
//         );

//         setSuccess(`Successfully imported ${response.data.imported} contacts`);

//         if (response.data.errors?.length > 0) {
//           setImportErrors(response.data.errors);
//         }

//         setTimeout(() => {
//           onImportSuccess();
//           setShow(false);
//         }, 2000);
//       } catch (err) {
//         setError(err.response?.data?.message || "Error importing contacts");
//       } finally {
//         setLoading(false);
//       }
//     };
//     reader.readAsArrayBuffer(file);
//   };

//   const downloadSampleFile = () => {
//     const sampleData = [
//       { Name: "John Doe", Phone: "+1234567890", Email: "john@example.com" },
//       { Name: "Jane Smith", Phone: "+1987654321", Email: "jane@example.com" },
//     ];
//     const worksheet = XLSX.utils.json_to_sheet(sampleData);
//     const workbook = XLSX.utils.book_new();
//     XLSX.utils.book_append_sheet(workbook, worksheet, "Contacts");
//     const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
//     const blob = new Blob([excelBuffer], {
//       type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
//     });
//     const url = window.URL.createObjectURL(blob);
//     const a = document.createElement("a");
//     a.href = url;
//     a.download = "sample_contacts_import.xlsx";
//     a.click();
//     window.URL.revokeObjectURL(url);
//   };

//   return (
//     <div
//       className={`modal fade ${show ? "show d-block" : ""}`}
//       tabIndex="-1"
//       role="dialog"
//     >
//       <div className="modal-dialog modal-lg" role="document">
//         <div className="modal-content">
//           <div className="modal-header">
//             <h5 className="modal-title">Import Contacts</h5>
//             <button
//               type="button"
//               className="btn-close"
//               onClick={() => setShow(false)}
//             ></button>
//           </div>
//           <div className="modal-body">
//             {error && <div className="alert alert-danger">{error}</div>}
//             {success && <div className="alert alert-success">{success}</div>}
//             {importErrors.length > 0 && (
//               <div className="alert alert-warning">
//                 <ul>
//                   {importErrors.map((err, i) => (
//                     <li key={i}>{err}</li>
//                   ))}
//                 </ul>
//               </div>
//             )}

//             <div className="mb-3">
//               <label className="form-label">Select Contact Bunch</label>
//               <select
//                 className="form-select"
//                 value={selectedBunch}
//                 onChange={(e) => setSelectedBunch(e.target.value)}
//               >
//                 <option value="">-- None --</option>
//                 {contactBunches.map((bunch) => (
//                   <option key={bunch.id} value={bunch.id}>
//                     {bunch.name}
//                   </option>
//                 ))}
//               </select>
//             </div>

//             <div className="mb-3">
//               <label className="form-label">Upload Excel File</label>
//               <input
//                 type="file"
//                 className="form-control"
//                 accept=".xlsx,.xls,.csv"
//                 onChange={handleFileChange}
//               />
//             </div>

//             <button
//               className="btn btn-outline-secondary w-100 mb-3"
//               onClick={downloadSampleFile}
//             >
//               Download Sample File
//             </button>

//             {previewData.length > 0 && (
//               <div className="table-responsive">
//                 <table className="table table-bordered table-sm">
//                   <thead>
//                     <tr>
//                       {Object.keys(previewData[0]).map((key) => (
//                         <th key={key}>{key}</th>
//                       ))}
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {previewData.map((row, i) => (
//                       <tr key={i}>
//                         {Object.keys(previewData[0]).map((key) => (
//                           <td key={key}>{row[key] || "-"}</td>
//                         ))}
//                       </tr>
//                     ))}
//                   </tbody>
//                 </table>
//               </div>
//             )}
//           </div>
//           <div className="modal-footer">
//             <button
//               className="btn btn-secondary"
//               onClick={() => setShow(false)}
//               disabled={loading}
//             >
//               Cancel
//             </button>
//             <button
//               className="btn btn-primary"
//               onClick={handleImport}
//               disabled={!file || loading}
//             >
//               {loading ? "Importing..." : "Import Contacts"}
//             </button>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default ContactImportModal;
